import { useState, useEffect, useCallback, useRef } from 'react';
import type { LoanType } from '@/types';
import type {
  MergedChecklistForm,
  ChecklistResponse,
  ChecklistStats,
  CategoryStats,
} from '@/types/checklist';
import {
  getChecklistForm,
  getChecklistFormForLead,
  toggleChecklistItem,
  updateChecklistItemNote,
  calculateChecklistStats,
  calculateCategoryStats,
} from '@/services/checklistService';
import { toast } from 'sonner';

// Cache for loaded checklists to avoid refetching
const checklistCache = new Map<string, MergedChecklistForm>();

interface UseChecklistOptions {
  /** Whether to skip loading on mount */
  skip?: boolean;
  /** Cache time in milliseconds (default: 5 minutes) */
  cacheTime?: number;
  /** Lead ID for version-aware checklist fetching */
  leadId?: string;
}

interface UseChecklistResult {
  /** The loaded checklist (null if not loaded yet) */
  checklist: MergedChecklistForm | null;
  /** Existing response for this lead */
  response: ChecklistResponse | null;
  /** Whether the checklist is currently loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Whether the checklist was loaded from cache */
  isFromCache: boolean;
  /** Whether the checklist version is locked (lead has existing response) */
  isVersionLocked: boolean;
  /** Overall checklist statistics */
  stats: ChecklistStats | null;
  /** Per-category statistics */
  categoryStats: CategoryStats[];
  /** Toggle a checklist item (check/uncheck) */
  toggleItem: (itemId: string) => Promise<void>;
  /** Add or update a note for an item */
  addNote: (itemId: string, note: string) => Promise<void>;
  /** Manually refresh the checklist */
  refresh: () => Promise<void>;
  /** Whether a save operation is in progress */
  isSaving: boolean;
}

/**
 * Hook for fetching and managing checklist with caching and version control
 * Supports version-aware checklist fetching when leadId is provided
 * 
 * @param loanType - The loan type to fetch the checklist for
 * @param lenderId - Optional lender ID for lender-specific checklists
 * @param options - Configuration options
 * @returns Checklist data, loading state, and mutation functions
 * 
 * @example
 * ```tsx
 * // Basic usage
 * const { checklist, toggleItem, stats } = useChecklist('home_loan', 'hdfc');
 * 
 * // With version locking for a lead
 * const { checklist, toggleItem, isVersionLocked } = useChecklist(
 *   lead.loanType,
 *   lead.lenderId,
 *   { leadId: lead.id }
 * );
 * 
 * // Check an item
 * await toggleItem('pan_card');
 * ```
 */
export const useChecklist = (
  loanType: LoanType | undefined,
  lenderId?: string,
  options: UseChecklistOptions = {}
): UseChecklistResult => {
  const { skip = false, cacheTime = 5 * 60 * 1000, leadId } = options;

  const [checklist, setChecklist] = useState<MergedChecklistForm | null>(null);
  const [response, setResponse] = useState<ChecklistResponse | null>(null);
  const [isLoading, setIsLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const cacheKeyRef = useRef<string>('');
  const cacheTimeRef = useRef<number>(0);

  // Generate cache key
  const getCacheKey = useCallback(() => {
    return leadId 
      ? `${loanType}_${lenderId || 'base'}_lead_${leadId}`
      : `${loanType}_${lenderId || 'base'}`;
  }, [loanType, lenderId, leadId]);

  /**
   * Fetch checklist from Firebase or cache
   */
  const fetchChecklist = useCallback(async () => {
    if (skip || !loanType) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const cacheKey = getCacheKey();
      const now = Date.now();
      
      // Check cache first (but not for version-locked leads)
      if (!leadId && checklistCache.has(cacheKey)) {
        const cachedChecklist = checklistCache.get(cacheKey)!;
        const cacheAge = now - cacheTimeRef.current;
        
        if (cacheAge < cacheTime) {
          console.log(`📦 Using cached checklist for ${cacheKey} (age: ${Math.round(cacheAge / 1000)}s)`);
          setChecklist(cachedChecklist);
          setResponse(cachedChecklist.existingResponse || null);
          setIsFromCache(true);
          setIsLoading(false);
          return;
        } else {
          console.log(`🔄 Cache expired for ${cacheKey}, fetching fresh data`);
          checklistCache.delete(cacheKey);
        }
      }

      // Fetch from Firebase
      let fetchedChecklist: MergedChecklistForm | null;
      
      if (leadId) {
        // Version-aware fetching for leads
        fetchedChecklist = await getChecklistFormForLead(loanType, lenderId, leadId);
      } else {
        // Regular fetching
        fetchedChecklist = await getChecklistForm(loanType, lenderId);
      }

      if (fetchedChecklist) {
        setChecklist(fetchedChecklist);
        setResponse(fetchedChecklist.existingResponse || null);
        
        // Cache only non-version-locked checklists
        if (!leadId) {
          checklistCache.set(cacheKey, fetchedChecklist);
          cacheTimeRef.current = now;
          cacheKeyRef.current = cacheKey;
        }
        
        setIsFromCache(false);
        console.log(`✅ Fetched checklist for ${loanType}${lenderId ? ` (${lenderId})` : ''}`);
      } else {
        throw new Error('Checklist not found');
      }
    } catch (err: any) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load checklist');
      toast.error('Failed to load checklist');
    } finally {
      setIsLoading(false);
    }
  }, [loanType, lenderId, leadId, skip, cacheTime, getCacheKey]);

  /**
   * Toggle a checklist item (check/uncheck)
   */
  const toggleItem = useCallback(async (itemId: string) => {
    if (!checklist || !leadId) {
      toast.error('Cannot toggle item: checklist or lead ID missing');
      return;
    }

    setIsSaving(true);
    try {
      // Get total items count
      const totalItems = checklist.categories.flatMap(cat => cat.items).length;
      
      // Toggle the item
      await toggleChecklistItem(
        leadId,
        itemId,
        checklist.id,
        checklist.version,
        checklist.version, // templateVersion same as version for now
        totalItems
      );
      
      // Refresh checklist to get updated response
      await fetchChecklist();
      
      // Success toast (brief)
      toast.success('Progress saved', { duration: 1500 });
    } catch (err: any) {
      console.error('Error toggling checklist item:', err);
      toast.error('Failed to save progress');
    } finally {
      setIsSaving(false);
    }
  }, [checklist, leadId, fetchChecklist]);

  /**
   * Add or update a note for a checklist item
   */
  const addNote = useCallback(async (itemId: string, note: string) => {
    if (!leadId) {
      toast.error('Cannot add note: lead ID missing');
      return;
    }

    setIsSaving(true);
    try {
      await updateChecklistItemNote(leadId, itemId, note);
      
      // Refresh to get updated response
      await fetchChecklist();
      
      toast.success('Note saved');
    } catch (err: any) {
      console.error('Error updating note:', err);
      toast.error(err.message || 'Failed to save note');
    } finally {
      setIsSaving(false);
    }
  }, [leadId, fetchChecklist]);

  /**
   * Manually refresh the checklist
   */
  const refresh = useCallback(async () => {
    // Clear cache for this checklist
    const cacheKey = getCacheKey();
    checklistCache.delete(cacheKey);
    
    await fetchChecklist();
  }, [getCacheKey, fetchChecklist]);

  // Calculate statistics
  const stats = checklist ? calculateChecklistStats(checklist, response) : null;
  const categoryStats = checklist
    ? checklist.categories.map(cat => calculateCategoryStats(cat, response))
    : [];

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  return {
    checklist,
    response,
    isLoading,
    error,
    isFromCache,
    isVersionLocked: checklist?.isVersionLocked || false,
    stats,
    categoryStats,
    toggleItem,
    addNote,
    refresh,
    isSaving,
  };
};
